/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmHero.module.scss';

const INBOUND_RECORDS = [
  { str: 'ACME MANUFACTURING LTD', sys: 'Salesforce', confidence: '0.98', delay: '0.5s' },
  { str: 'Acme Mfg. Limited', sys: 'SAP', confidence: '0.96', delay: '1.1s' },
  { str: 'acme manufacturing co', sys: 'Billing', confidence: '0.93', delay: '1.7s' },
];

const STATS = [
  { value: 600, suffix: 'M+', label: 'records resolvable' },
  { value: 400, suffix: '+', label: 'registries read live' },
];

export const MdmHero = () => {
  const { t } = useInt();
  const stageRef = useRef<HTMLDivElement>(null);
  const [counts, setCounts] = useState(() => STATS.map(() => 0));

  const handlePointerMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const el = stageRef.current;

    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;

    el.style.setProperty('--rx', `${(py - 0.5) * -6}deg`);
    el.style.setProperty('--ry', `${(px - 0.5) * 8}deg`);
    el.style.setProperty('--mx', `${px * 100}%`);
    el.style.setProperty('--my', `${py * 100}%`);
  }, []);

  const handlePointerLeave = useCallback(() => {
    const el = stageRef.current;

    if (!el) {
      return;
    }

    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setCounts(STATS.map((s) => s.value));
      return undefined;
    }

    const duration = 1400;
    const start = performance.now() + 300;
    let frame: number;

    const tick = (now: number) => {
      const progress = Math.min(1, Math.max(0, (now - start) / duration));
      const eased = 1 - (1 - progress) ** 3;

      setCounts(STATS.map((s) => Math.round(s.value * eased)));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <Section className={styles.root}>
      <div className={styles.orbIndigo} aria-hidden="true" />
      <div className={styles.orbBlue} aria-hidden="true" />

      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            title={t('Master data management, anchored to the registry of record.')}
            description={t(
              'Match, dedupe, and enrich every customer, supplier, and prospect record — resolved to its official registration number, with the filing behind each field and the date it was read. Stewards decide on evidence, not on a score.',
            )}
          />

          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="/book-a-demo">
              {t('Match a sample file')}
            </Button>

            <Button type="dark-outline" size="xxl" href="/data-dictionary">
              {t('View data dictionary')}
            </Button>
          </div>

          <div className={styles.stats}>
            {STATS.map((stat, i) => (
              <div key={stat.label} className={styles.stat}>
                <b>
                  {counts[i]}
                  {stat.suffix}
                </b>
                <span>{t(stat.label)}</span>
              </div>
            ))}
          </div>
        </div>

        <div ref={stageRef} className={styles.stage} onMouseMove={handlePointerMove} onMouseLeave={handlePointerLeave}>
          <div className={styles.cardGlow} aria-hidden="true" />

          <div
            className={styles.card}
            aria-label="Entity resolution: three inbound records resolve to one registry entity, ACME MANUFACTURING LIMITED"
          >
            <div className={styles.glare} aria-hidden="true" />

            <div className={styles.cardHead}>
              <span className={styles.cardLabel}>{t('Entity resolution')}</span>
              <span className={styles.sampleTag}>{t('Sample')}</span>
              <span className={styles.live}>
                <span className={styles.liveDot} />
                {t('Synced daily')}
              </span>
            </div>

            <div className={styles.cardBody}>
              {INBOUND_RECORDS.map((r) => (
                <div key={r.str} className={styles.inRow} style={{ '--d': r.delay } as CSSProperties}>
                  <div>
                    <span className={styles.inStr}>{r.str}</span>
                    <span className={styles.inSys}>{r.sys}</span>
                  </div>
                  <div className={styles.inStatus}>
                    <i className={styles.matching}>{t('Matching')}</i>
                    <i className={styles.matched}>{`✓ ${r.confidence}`}</i>
                  </div>
                </div>
              ))}

              <div className={styles.merge}>
                <span>3 records</span>
                <span className={styles.mergeLine} />
                <span>1 entity</span>
              </div>

              <div className={styles.goldLabel}>{t('Registry of record')}</div>
              <div className={styles.gold}>
                <div className={styles.goldName}>ACME MANUFACTURING LIMITED</div>

                <div className={styles.goldChips}>
                  <span className={styles.activeChip}>{t('Active')}</span>
                </div>

                <dl className={styles.fields}>
                  <dt>{t('Reg. number')}</dt>
                  <dd>04123987</dd>
                  <dt>{t('Registry')}</dt>
                  <dd>Companies House · GB</dd>
                </dl>

                <div className={styles.provenance}>
                  <span>companies_house/filing</span>
                  <span>2026-07-21</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
